import { RefObject, useEffect, useState } from 'react';

/**
 * Position for a filter panel rendered via portal (see ColumnFilterDropdown/
 * OrgColumnFilter) — needed because `.users-table-container` sets `overflow-y:
 * hidden` (to avoid a stray vertical scrollbar alongside its horizontal one), which
 * clips any absolutely-positioned descendant that pops below it. Rendering the panel
 * into `document.body` with `position: fixed` at the anchor's real screen coordinates
 * sidesteps that clipping entirely.
 */
export function useFilterPanelPosition(
  open: boolean,
  anchorRef: RefObject<HTMLElement>
): { top: number; left: number } | null {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    // Clamp to the viewport: the panel is `position: fixed`, so one that overflows the
    // right edge (the right-most columns' funnels sit close to it) can't be scrolled
    // into view — its Apply button would be permanently unreachable. 260px covers both
    // the org panel (`.column-filter__panel--org`, 260px) and the checkbox panel
    // (`.column-filter__panel`, 180px min-width).
    const PANEL_WIDTH = 260;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));
    setPos({ top: rect.bottom + 6, left });
  }, [open, anchorRef]);

  return pos;
}

/**
 * The open/dismiss/position behaviour every column-filter panel shares.
 *
 * ColumnFilterDropdown (checkbox lists), ColumnFilterPopover (text and numeric
 * ranges) and the platform dashboard's HeaderFilterPopover each used to carry their
 * own copy of this: the same outside-click handler, the same close-on-scroll effect,
 * the same call into useFilterPanelPosition. Three copies meant a fix to the shared
 * behaviour — closing on Escape, say — had to be made three times or the filters
 * would quietly start behaving differently from one table to the next.
 *
 * Callers own only what differs: the funnel's markup and the panel's contents.
 */
export function useFilterPanel(
  anchorRef: RefObject<HTMLElement>,
  panelRef: RefObject<HTMLElement>
): { open: boolean; setOpen: (open: boolean) => void; position: { top: number; left: number } | null } {
  const [open, setOpen] = useState(false);
  const position = useFilterPanelPosition(open, anchorRef);

  // Dismiss on a click outside both the funnel and its panel. Clicks inside either
  // are the user operating the filter, not leaving it.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, anchorRef, panelRef]);

  // The panel is `position: fixed` at a coordinate captured once on open — cheaper
  // than tracking the anchor continuously, at the cost of staying put if the page
  // moves. Close it rather than let it drift away from the funnel it belongs to.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return { open, setOpen, position };
}
